import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import LogRocket from 'logrocket';
import { nanoid } from 'nanoid';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  createInstantSurvey,
  DEFAULT_GROUP_NAME,
  MAINTENANCE_MESSAGE,
  readInstantSurvey,
  readInstantSurveyType,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  updateInstantSurvey,
} from '@api/instant_survey';
import { readSurvey, readSurveyType, surveyStatus } from '@api/surveys';
import { readClientSubscription } from '@api/client';

import { GATES } from '@constants';
import { updateUserCreditsRemaining } from '@redux/slices/user';
import {
  Button,
  CheckSquareIcon,
  Error,
  LoaderSkeleton,
  MaintenanceView,
  ModalV2 as Modal,
  ShieldTipIcon,
  toast,
  useAutoOpenAfterDelay,
} from '@utilities';

import Column from '../../layouts/Column';
import Confirmation from './components/Confirmation';
import ModalUnsavedChanges from './components/ModalUnsavedChanges';
import Panelists from './components/Panelists';
import Questionnaire from './components/Questionnaire';
import Review from './components/Review';
import SurveyNetworkError from '../../components/SurveyNetworkError';

import { payWithCompletes, payWithCredits } from '../Checkout/utilities/constants';

import { SURVEY_STEPS, SURVEY_TEMPORARY_TITLE } from './utilities/constants';
import { SurveyContext } from './utilities/context';
import {
  getCurrentQuestionnairePrice,
  getModalConfig,
  getQuestionsCost,
  getQuotaLimit,
} from './utilities/helpers';

import { ReactComponent as NumeratorLogo } from '../../components/Header/assets/data-wave-logo.svg';

import styles from './_index.module.scss';

const CreateInstantSurvey = () => {
  const [activeStep, setActiveStep] = useState(SURVEY_STEPS.PANELISTS);
  const [error, setError] = useState();
  const [hasLaunched, setHasLaunched] = useState(false);
  const [insightsDocument, setInsightsDocument] = useState();
  const [insightsDocumentType, setInsightsDocumentType] = useState();
  const [instantSurveyType, setInstantSurveyType] = useState();
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isSaveAsDraftOpen, setIsSaveAsDraftOpen] = useState(false);
  const [isSavingAsDraft, setIsSavingAsDraft] = useState(false);
  const [jobId, setJobId] = useState('');
  const [showAIGenerationModal, setShowAIGenerationModal] = useState(false);
  const [showSurveyTemplateModal, setShowSurveyTemplateModal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [surveyGoals, setSurveyGoals] = useState('');

  const userData = useSelector((state) => state?.user?.data);
  const isSubscribed = !!userData?.clientInfo?.subscriptions?.length;

  const [launchMethod, setLaunchMethod] = useState(payWithCredits.key);

  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const isInstantSurveyMaintenance = client.checkGate(GATES.INSTANT_SURVEY_MAINTENANCE);

  const { instant_survey_type_id: instantSurveyTypeId } = useParams();

  const { panel } = userData || {};
  const defaultResponseCount = getQuotaLimit(instantSurveyTypeId);

  const [survey, setSurvey] = useState({
    country: panel?.abbrev?.toLowerCase(),
    groups: [
      {
        genRepSampleType: null,
        id: nanoid(),
        name: DEFAULT_GROUP_NAME,
        promptAnswers: [],
        quotaLimit: defaultResponseCount,
      },
    ],
    id: null,
    instantSurveyType: instantSurveyTypeId,
    questionLogic: {},
    questions: [],
    researchObjectives: '',
    surveyTemplateId: null,
    title: '',
  });

  const isMonadicConceptTest =
    instantSurveyType?.id === 'monadic-concept-test-v3-instant-survey-type';

  const isTURF = instantSurveyType?.id === 'turf-v3-instant-survey-type';
  const isNewItemSurvey = instantSurveyType?.id === 'new-item-v3-instant-survey-type';
  const isVideoSurvey = instantSurveyType?.id === 'video-survey-v3-instant-survey-type';
  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);
  const activeQuestions = survey.questions.filter(({ _delete }) => !_delete);

  const currentQuestionCount = getQuestionsCost(activeQuestions);

  const currentQuestionnairePrice = useMemo(
    () =>
      getCurrentQuestionnairePrice({
        questionnairePricing: insightsDocumentType?.questionnairePricing,
        survey,
      }),
    [survey]
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const insightsId = searchParams.get('id');

  const infoModalSurveyType = useMemo(() => {
    if (isMonadicConceptTest) {
      return 'monadic';
    } else if (isTURF) {
      return 'turf';
    } else if (isNewItemSurvey) {
      return 'new-item';
    }
  }, [isMonadicConceptTest, isTURF, isNewItemSurvey]);

  const infoModalConfig =
    !insightsId && infoModalSurveyType ? getModalConfig({ surveyType: infoModalSurveyType }) : null;

  const { close: closeInfoModal, isOpen: isInfoModalOpen } = useAutoOpenAfterDelay(
    500,
    infoModalSurveyType
  );

  const shouldBlock = useCallback(() => {
    return (
      !(hasLaunched || isInstantSurveyMaintenance || isSavingAsDraft) &&
      (survey.title.trim() ||
        survey.groups.some(
          ({ promptAnswers }) => promptAnswers.filter(({ _delete }) => !_delete).length > 0
        ))
    );
  }, [hasLaunched, isInstantSurveyMaintenance, isSavingAsDraft, survey]);
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!isStatsigLoading && !isInstantSurveyMaintenance) {
      fetchSurveyData();
      if (isSubscribed) {
        fetchSubscription();
      }
    }
  }, [isStatsigLoading]);

  const fetchSubscription = async () => {
    setSubscriptionError(null);
    setIsLoadingSubscription(true);

    try {
      const subscriptionData = await readClientSubscription();
      setSubscription(subscriptionData[0]);
      if (subscriptionData[0]?.hasStarted) setLaunchMethod(payWithCompletes.key);
    } catch (err) {
      setSubscriptionError(err);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const fetchSurveyData = async () => {
    try {
      const surveyRequests = [
        readInstantSurveyType({ id: instantSurveyTypeId }),
        readSurveyType({ documentId: instantSurveyTypeId }),
      ];
      const instantSurveyId = searchParams.get('ref');
      const isModal = searchParams.get('modal');
      const hasRefOnly = instantSurveyId && !insightsId;

      if (insightsId && instantSurveyId) {
        surveyRequests.push(
          readInstantSurvey({ id: instantSurveyId }),
          readSurvey({ id: insightsId })
        );
      }
      if ((instantSurveyId && isModal) || hasRefOnly) {
        surveyRequests.push(readInstantSurvey({ id: instantSurveyId }));
      }

      const [
        instantSurveyTypeResponse,
        insightsDocumentTypeResponse,
        instantSurveyDraftResponse,
        insightsDocumentResponse,
      ] = await Promise.all(surveyRequests);

      const insightsDocumentStatus = insightsDocumentResponse?.status;
      if (insightsDocumentStatus && insightsDocumentStatus !== surveyStatus.DRAFT) {
        toast(
          'The survey you are looking for is not available to edit. Please search for it in your Workspace.'
        );
        navigate('/dashboard/workspace/surveys?filters=');
        return;
      }

      setInstantSurveyType(instantSurveyTypeResponse);
      setInsightsDocumentType({
        ...insightsDocumentTypeResponse,
        samplePricing: insightsDocumentTypeResponse.samplePricing.filter(
          ({ isAskWhy, type }) => !isAskWhy && type === 'CREDITS'
        ),
      });

      if (insightsId && instantSurveyId) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        // if (hasQuestionLogicError) {
        //   toast(
        //     'There was an error while rebuilding your questionnaire logic. Please confirm your selections before continuing.',
        //     { status: 'error' }
        //   );
        //   setHadQuestionLogicError(true);
        // }

        setInsightsDocument(insightsDocumentResponse);
        const savedLaunchMethod = insightsDocumentResponse?.paymentType;

        if (savedLaunchMethod) {
          const launchMethod =
            savedLaunchMethod === payWithCompletes.key && isSubscribed
              ? payWithCompletes.key
              : payWithCredits.key;

          setLaunchMethod(launchMethod);
        }

        setSurvey(surveyData);
      } else if ((instantSurveyId && isModal) || hasRefOnly) {
        const { hasQuestionLogicError, ...surveyData } = instantSurveyDraftResponse;
        setSurvey({
          ...surveyData,
          title: surveyData.title === SURVEY_TEMPORARY_TITLE ? '' : surveyData.title,
        });
      }

      setIsLoadingSurvey(false);
    } catch (error) {
      console.error(error);
      setError(error);
    }
  };

  const handleSurveyPublish = ({ userCreditsRemaining }) => {
    LogRocket.track('InstantSurvey_Published', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyTypeId,
    });
    dispatch(updateUserCreditsRemaining({ userCreditsRemaining }));
    setHasLaunched(true);
  };

  const saveInstantSurvey = async ({ signal } = {}) => {
    const { id, title, ...surveyData } = survey;
    let response;

    if (!id) {
      response = await createInstantSurvey({
        instantSurvey: {
          ...surveyData,
          status: 'DRAFT',
          title: title || SURVEY_TEMPORARY_TITLE,
        },
        signal,
      });
    } else {
      response = await updateInstantSurvey({
        instantSurvey: {
          ...surveyData,
          id,
          title: title || SURVEY_TEMPORARY_TITLE,
        },
        signal,
      });
    }

    const { title: savedSurveyTitle, ...surveyReponse } = response;
    setSurvey({
      title: savedSurveyTitle === SURVEY_TEMPORARY_TITLE ? '' : savedSurveyTitle,
      ...surveyReponse,
    });

    return response;
  };

  const updateSurvey = (fields) => {
    // callback function necessary as consecutive update calls are necessary when reordering questions
    setSurvey((surveyState) => ({ ...surveyState, ...fields }));
  };

  if (isInstantSurveyMaintenance) {
    return <MaintenanceView heading={MAINTENANCE_MESSAGE} logo={<NumeratorLogo />} />;
  }
  if (error) {
    return error?.code === 'ERR_NETWORK' ? (
      <SurveyNetworkError />
    ) : (
      <Error status={error?.response?.status} />
    );
  }

  if (hasLaunched) return <Confirmation />;
  if (isLoadingSurvey || isStatsigLoading) {
    return (
      <LoaderSkeleton height={450}>
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="0" />
        <rect height="130" rx="4" ry="4" width="1333" x="0" y="60" />
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="210" />
        <rect height="20" rx="4" ry="4" width="1333" x="0" y="270" />
        <rect height="20" rx="2" ry="2" width="175" x="0" y="310" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="350" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="375" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="400" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="425" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="450" />
      </LoaderSkeleton>
    );
  }

  return (
    <SurveyContext.Provider
      value={{
        activeGroups,
        activeQuestions,
        activeStep,
        currentQuestionCount,
        currentQuestionnairePrice,
        insightsDocument,
        insightsDocumentType,
        instantSurveyType,
        isLoadingSubscription,
        isResearchObjectivesTooLong:
          survey.researchObjectives?.length > RESEARCH_OBJECTIVES_MAX_LENGTH,
        isSaveAsDraftOpen,
        isSavingAsDraft,
        isSubscribed,
        jobId,
        launchMethod,
        maxQuestions: isVideoSurvey ? 1 : insightsDocumentType.maxQuestions,
        saveInstantSurvey,
        setActiveStep,
        setIsSaveAsDraftOpen,
        setIsSavingAsDraft,
        setJobId,
        setLaunchMethod,
        setShowAIGenerationModal,
        setShowSurveyTemplateModal,
        setSurveyGoals,
        showAIGenerationModal,
        showSurveyTemplateModal,
        subscription,
        subscriptionError,
        survey,
        surveyGoals,
        updateSurvey,
      }}
    >
      {activeStep === SURVEY_STEPS.PANELISTS && (
        <Panelists
          defaultResponseCount={defaultResponseCount}
          onNext={() => setActiveStep(SURVEY_STEPS.QUESTIONNAIRE)}
        />
      )}
      {activeStep === SURVEY_STEPS.QUESTIONNAIRE && (
        <Questionnaire
          onNext={() => setActiveStep(SURVEY_STEPS.REVIEW)}
          onPrevious={() => setActiveStep(SURVEY_STEPS.PANELISTS)}
        />
      )}
      {activeStep === SURVEY_STEPS.REVIEW && (
        <Review onPublish={handleSurveyPublish} setActiveStep={setActiveStep} />
      )}
      {blocker?.state === 'blocked' && (
        <ModalUnsavedChanges onClose={blocker.reset} onContinue={blocker.proceed} />
      )}
      {infoModalConfig && (
        <Modal
          buttons={[<Button key="close" onClick={closeInfoModal} text="Close" variant="primary" />]}
          className={styles['create-instant-survey-info-modal']}
          isActive={isInfoModalOpen}
          onClose={closeInfoModal}
          title={infoModalConfig.title}
        >
          <p>
            {infoModalConfig.description}{' '}
            <a href={infoModalConfig.link} rel="noreferrer" target="_blank">
              Learn more
            </a>
          </p>
          <h3>
            <ShieldTipIcon />
            Setup
          </h3>
          <ol>
            {infoModalConfig.steps.map((step) => (
              <li key={step}>
                <CheckSquareIcon />
                {step}
              </li>
            ))}
          </ol>
        </Modal>
      )}
    </SurveyContext.Provider>
  );
};

const CreateInstantSurveyLayout = () => (
  <Column>
    <CreateInstantSurvey />
  </Column>
);

export default CreateInstantSurveyLayout;
